import React, { useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";
import Loading from "../../common/components/loading/loading";
import {
  appendBandTooltipTargets,
  buildTooltipHtml,
  createTooltipHandlers,
  formatTooltipDate,
  formatTooltipNumber,
  removeTooltip,
} from "../../common/components/d3/d3ChartShared";

const ClusteredBarChart = ({ data, title, isLoading }) => {
  const svgRef = useRef();
  const tooltipRef = useRef(null);

  const renderChart = useCallback(() => {
    const actualColor = "rgba(41, 137, 83, 0.84)";
    const avgDayColor = "rgba(196, 61, 52, 0.84)";
    const peakHourColor = "rgba(35, 129, 218, 0.82)";
    const capacityColor = "rgba(117, 123, 132, 0.82)";

    // Clear the SVG before drawing new content
    d3.select(svgRef.current).selectAll("*").remove();

    // Dimensions
    const svg = d3.select(svgRef.current);

    if (!svg.node()) {
      return;
    }

    const parentContainer = svg.node().parentNode;
    const containerWidth = parentContainer.clientWidth;
    const containerHeight = parentContainer.clientHeight;

    const margin = { top: 30, right: 30, bottom: 30, left: 60 };
    const width = containerWidth - margin.left - margin.right;
    const height = containerHeight - margin.top - margin.bottom;

    if (!height || height <= 0 || !width) return;

    // Parse date format for `forecastTransactionDate` in UTC to avoid time zone issues
    const parseDate = d3.timeParse("%Y-%m-%dT%H:%M:%SZ");

    // Format data, converting dates and calculating daily summaries
    const formattedData = data
      .filter(
        (d) =>
          d.forecastTransactionDate && d.mostLikely !== null && d.mcr !== null,
      ) // Ensure valid data
      .map((d) => ({
        forecastTransactionDate: parseDate(d.forecastTransactionDate),
        mostLikely: d.mostLikely,
        mcr: d.mcr,
        actual: d.actual,
      }));

    const normalizedData = formattedData.map((d) => ({
      ...d,
      normalizedDate: d3.timeDay(d.forecastTransactionDate), // Normalize to day
    }));

    // Calculate daily summaries (avgMostLikely, maxMostLikely, maxMcr)
    const dailySummaryMap = d3.rollups(
      normalizedData,
      (v) => ({
        avgMostLikely: d3.mean(v, (d) => d.mostLikely),
        maxMostLikely: d3.max(v, (d) => d.mostLikely),
        avgActual: d3.mean(v, (d) => d.actual),
        maxMcr: d3.max(v, (d) => d.mcr),
      }),
      (d) => +d.normalizedDate, // Use +d.normalizedDate to ensure consistent Map key format
    );

    // Create a Map for faster lookup of daily summaries by date
    const dailySummaryFilter = new Map(dailySummaryMap);

    // Create an array for the clustered bars data
    const barsData = Array.from(
      dailySummaryFilter,
      ([date, { avgActual, avgMostLikely, maxMostLikely, maxMcr }]) => ({
        date,
        avgActual,
        avgMostLikely,
        maxMostLikely,
        maxMcr,
      }),
    );

    // Create SVG
    const chartSvg = svg
      .append("svg")
      .attr("width", "100%")
      .attr("height", "100%")
      .attr("viewBox", `0 0 ${containerWidth} ${containerHeight}`)
      .attr("preserveAspectRatio", "none") // Preserve aspect ratio
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Set up scales
    const xScale = d3
      .scaleBand()
      .domain(barsData.map((d) => d.date))
      .range([0, width])
      .padding(0.3); // Increased padding for spacing between clusters

    const yScale = d3
      .scaleLinear()
      .domain([
        0,
        d3.max(barsData, (d) =>
          Math.max(d.avgMostLikely, d.maxMostLikely, d.maxMcr),
        ),
      ])
      .nice()
      .range([height, 0]);

    // X and Y Axes
    chartSvg
      .append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(xScale).tickFormat(d3.timeFormat("%Y-%m-%d")))
      .attr("class", "d3-chart-axis");

    chartSvg
      .append("g")
      .call(d3.axisLeft(yScale))
      .attr("class", "d3-chart-axis");

    // Y Axis label
    chartSvg
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -height / 2)
      .attr("y", -margin.left + 10)
      .attr("text-anchor", "middle")
      .attr("font-size", "14px")
      .attr("class", "d3-chart-axis-label")
      .text("MW/h");

    // Add gridlines
    chartSvg
      .append("g")
      .attr("class", "grid  d3-chart-gridline")
      .call(d3.axisLeft(yScale).tickSize(-width).tickFormat(""));

    // Bars for daily summaries
    const barGroups = chartSvg
      .selectAll(".bar-group")
      .data(barsData)
      .enter()
      .append("g")
      .attr("class", "bar-group")
      .attr("transform", (d) => `translate(${xScale(d.date)},0)`);

    const barWidth = xScale.bandwidth() / 4 - 1; // 4 bars per cluster, small gap

    // Avg Actual (green)
    barGroups
      .append("rect")
      .attr("class", "bar dashboard-bar avgActual")
      .attr("x", 0)
      .attr("y", (d) => yScale(d.avgActual ?? 0))
      .attr("width", barWidth)
      .attr("rx", 3)
      .attr("ry", 3)
      .attr("height", (d) => Math.max(0, height - yScale(d.avgActual ?? 0)))
      .attr("fill", actualColor);

    // Avg Most Likely (red)
    barGroups
      .append("rect")
      .attr("class", "bar dashboard-bar avgMostLikely")
      .attr("x", barWidth + 1)
      .attr("y", (d) => yScale(d.avgMostLikely ?? 0))
      .attr("width", barWidth)
      .attr("rx", 3)
      .attr("ry", 3)
      .attr("height", (d) => Math.max(0, height - yScale(d.avgMostLikely ?? 0)))
      .attr("fill", avgDayColor);

    // Max Most Likely (blue)
    barGroups
      .append("rect")
      .attr("class", "bar dashboard-bar maxMostLikely")
      .attr("x", (barWidth + 1) * 2)
      .attr("y", (d) => yScale(d.maxMostLikely ?? 0))
      .attr("width", barWidth)
      .attr("rx", 3)
      .attr("ry", 3)
      .attr("height", (d) => Math.max(0, height - yScale(d.maxMostLikely ?? 0)))
      .attr("fill", peakHourColor);

    // Max MCR (grey)
    barGroups
      .append("rect")
      .attr("class", "bar dashboard-bar maxMcr")
      .attr("x", (barWidth + 1) * 3)
      .attr("y", (d) => yScale(d.maxMcr ?? 0))
      .attr("width", barWidth)
      .attr("rx", 3)
      .attr("ry", 3)
      .attr("height", (d) => Math.max(0, height - yScale(d.maxMcr ?? 0)))
      .attr("fill", capacityColor);

    const { showTooltip, hideTooltip } = createTooltipHandlers({
      tooltipRef,
    });

    appendBandTooltipTargets({
      chartSvg,
      data: barsData,
      xScale,
      innerHeight: height,
      xAccessor: (d) => d.date,
      showTooltip,
      hideTooltip,
      htmlAccessor: (d) =>
        buildTooltipHtml({
          title: formatTooltipDate(new Date(Number(d.date)), "%Y-%m-%d"),
          rows: [
            {
              label: "Avg Actual",
              value: `${formatTooltipNumber(d.avgActual, { maximumFractionDigits: 0 })} MW`,
              color: actualColor,
            },
            {
              label: "Avg/Day",
              value: `${formatTooltipNumber(d.avgMostLikely, { maximumFractionDigits: 0 })} MW`,
              color: avgDayColor,
            },
            {
              label: "Peak Hour",
              value: `${formatTooltipNumber(d.maxMostLikely, { maximumFractionDigits: 0 })} MW`,
              color: peakHourColor,
            },
            {
              label: "Capacity",
              value: `${formatTooltipNumber(d.maxMcr, { maximumFractionDigits: 0 })} MW`,
              color: capacityColor,
            },
          ],
        }),
    });

    // Legend
    const legend = chartSvg
      .append("g")
      .attr("transform", `translate(${width / 2}, -10)`); // Position legend above the bars

    // Add title
    chartSvg
      .append("text")
      .attr("x", width / 2)
      .attr("y", -margin.top / 2)
      .attr("text-anchor", "middle")
      .attr("font-size", "16px")
      .attr("font-weight", "bold")
      .text(title)
      .attr("class", "d3-chart-title");

    

    // Legend: Avg Actial
    legend
      .append("rect")
      .attr("x", width / 4 - 145)
      .attr("y", 0)
      .attr("width", 10)
      .attr("height", 10)
      .attr("fill", actualColor);

    legend
      .append("text")
      .attr("x", width / 4 - 130)
      .attr("y", 10)
      .attr("font-size", "12px")
      .attr("class", "d3-chart-legend-label")
      .text("Avg Actual");

    

    // Legend: Avg/Day
    legend
      .append("rect")
      .attr("x", width / 4 - 60)
      .attr("y", 0)
      .attr("width", 10)
      .attr("height", 10)
      .attr("fill", avgDayColor);

    legend
      .append("text")
      .attr("x", width / 4 - 45)
      .attr("y", 10)
      .attr("font-size", "12px")
      .attr("class", "d3-chart-legend-label")
      .text("Avg/Day");

    

    // Legend: Peak Hour
    legend
      .append("rect")
      .attr("x", width / 4 + 10)
      .attr("y", 0)
      .attr("width", 10)
      .attr("height", 10)
      .attr("fill", peakHourColor);

    legend
      .append("text")
      .attr("x", width / 4 + 25)
      .attr("y", 10)
      .attr("font-size", "12px")
      .attr("class", "d3-chart-legend-label")
      .text("Peak Hour");

    

    // Legend: Capacity
    legend
      .append("rect")
      .attr("x", width / 4 + 90)
      .attr("y", 0)
      .attr("width", 10)
      .attr("height", 10)
      .attr("fill", capacityColor);

    legend
      .append("text")
      .attr("x", width / 4 + 105)
      .attr("y", 10)
      .attr("font-size", "12px")
      .attr("class", "d3-chart-legend-label")
      .text("Capacity");
  }, [data, title]);

  useEffect(() => {
    !isLoading && renderChart();

    return () => {
      removeTooltip(tooltipRef);
    };
  }, [renderChart, isLoading]);

  useEffect(() => {
    let resizeTimeout = null;

    const svgNode = svgRef.current; // <- capture current value
    if (!svgNode) return;

    const throttledResize = (entries) => {
      if (!resizeTimeout) {
        resizeTimeout = setTimeout(() => {
          if (!isLoading) {
            renderChart();
          }
          resizeTimeout = null;
        }, 100); // throttle duration in ms
      }
    };

    const observer = new ResizeObserver(throttledResize);

    if (svgNode) {
      observer.observe(svgNode.parentNode);
    }

    return () => {
      if (svgNode && svgNode.parentNode) {
        observer.unobserve(svgNode.parentNode);
      }
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
        resizeTimeout = null;
      }
    };
  }, [renderChart, isLoading]);

  if (isLoading ?? true) return <Loading />;
  return <svg ref={svgRef} className="dashboard-d3-chart" width="100%" height="100%"></svg>;
};

export default ClusteredBarChart;





















